/**
 * Code Generation Modal Component
 * Modal for generating redeem codes with count validation and CSV download
 * Validates count range (1-10000) and triggers CSV download on success
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useGenerateCodes } from '../../hooks/useRedeemGroupApi';
import { FormButton } from '../form/FormButton';
import { AnimatedMessage } from '../ui/AnimatedMessage';
import { cn } from '../../lib/utils';
import { generateCSV } from '../../lib/utils/csv';

interface CodeGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
}

// Form input type
interface CodeGenerationFormInput {
  count: number;
  note?: string;
}

// Code generation validation schema (1-10000 range matches backend)
const codeGenerationSchema = z.object({
  count: z
    .number()
    .int('Count must be a whole number')
    .min(1, 'Count must be at least 1')
    .max(10000, 'Count cannot exceed 10,000'),
  note: z.string().max(200, 'Note cannot exceed 200 characters').optional(),
});

export function CodeGenerationModal({
  isOpen,
  onClose,
  groupId,
  groupName,
}: CodeGenerationModalProps) {
  const generateCodes = useGenerateCodes();
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CodeGenerationFormInput>({
    resolver: zodResolver(codeGenerationSchema),
    defaultValues: {
      count: 100,
    },
    mode: 'onChange',
  });

  // Reset form and success state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset({ count: 100 });
      setDownloadSuccess(false);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: CodeGenerationFormInput) => {
    try {
      const trimmedNote = data.note?.trim();
      const result = await generateCodes.mutateAsync({
        groupId,
        count: data.count,
        ...(trimmedNote && { note: trimmedNote }),
      });

      // Generate and download CSV if codes were returned
      if (result?.codes && result.codes.length > 0) {
        try {
          generateCSV(result.codes, groupName);
          setDownloadSuccess(true);

          // Small delay to ensure download completes before modal closes
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (downloadError) {
          console.error('CSV download failed:', downloadError);
          // Continue to close modal even if download fails
        }
      }

      onClose();
    } catch (_error) {
      // Error is handled by the mutation
    }
  };

  const handleClose = () => {
    if (generateCodes.isPending) return;
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const isLoading = generateCodes.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={isLoading ? undefined : handleClose}
        />

        {/* Modal */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="code-generation-modal-title"
          className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3
              id="code-generation-modal-title"
              className="text-lg font-medium text-gray-900 dark:text-gray-100"
            >
              Generate Codes
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Group Name Display */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Redeem Group
            </p>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">
              {groupName}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Code Count */}
            <div>
              <label
                htmlFor="count"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Number of Codes *
              </label>
              <input
                {...register('count', { valueAsNumber: true })}
                id="count"
                type="number"
                min={1}
                max={10000}
                step={1}
                placeholder="Enter number of codes to generate"
                disabled={isLoading}
                className={cn(
                  'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm',
                  errors.count &&
                    'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter a value between 1 and 10,000
              </p>
              {errors.count && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.count.message}
                </p>
              )}
            </div>

            {/* Note Field */}
            <div>
              <label
                htmlFor="note"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Note (Optional)
              </label>
              <input
                {...register('note')}
                id="note"
                type="text"
                maxLength={200}
                placeholder="e.g., VIP tickets for Event X"
                disabled={isLoading}
                className={cn(
                  'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm',
                  errors.note &&
                    'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional description to help identify this batch (max 200
                characters)
              </p>
              {errors.note && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.note.message}
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Codes will be generated and downloaded as a CSV file with three
                columns: CODE, FULL_URL, and GENERATED_AT
              </p>
            </div>

            {/* Success/Error Messages */}
            {downloadSuccess ? (
              <AnimatedMessage
                type="success"
                message="CSV download started successfully!"
              />
            ) : null}
            {generateCodes.error ? (
              <AnimatedMessage
                type="error"
                message={
                  generateCodes.error instanceof Error
                    ? generateCodes.error.message
                    : 'An error occurred'
                }
              />
            ) : null}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <FormButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </FormButton>
              <FormButton
                type="submit"
                variant="primary"
                disabled={!isValid || isLoading}
                isLoading={isLoading}
                loadingText="Generating..."
                className="flex-1"
              >
                Generate & Download
              </FormButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
